// src/components/demo/DemoLekhiPage.js
// DEMO (offline, no database): mirrors the new Lekhi today screen, shortened for demos.
// Card 1 (Grammar) is already done; card 2 (Maths) can be explored.
import React, { useState, useEffect, useRef } from 'react';
import AudioView from '../lekhi/AudioView';
import TextView from '../lekhi/TextView';
import MCQs from '../lekhi/MCQs';
import { LekhiWatermark, lekhiProtectProps, LEKHI_PROTECT_CLASS } from '../lekhi/LekhiProtect';
import { getSubjectName, getTopicNameMr } from '../../services/lekhiTopicNames';
import '../lekhi/LekhiTrainingPage.css';
import '../lekhi/LekhiTodayPage.css';
import './DemoCelebration.css';

const CONTAINER_PROPS = {
  className: `lekhi-training-container ${LEKHI_PROTECT_CLASS}`,
  ...lekhiProtectProps
};
const RESULT_SCREEN_MS = 5000;
const REWARD_DELAY_MS = 3000;

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const AUDIO_DATA = { audioURL: '/lcm_hcf_audio.mp3' };
const preloadAudio = new Audio('/lcm_hcf_audio.mp3');
preloadAudio.preload = 'auto';

const STUDY_TEXT = `## मसावि म्हणजे काय?
दोन किंवा अधिक संख्यांना **पूर्ण भाग जाणारी सर्वात मोठी संख्या** म्हणजे **मसावि** (महत्तम सामाईक विभाजक).
उदाहरण: १२ आणि १८ चे विभाजक पाहू.
- १२ : १, २, ३, ४, ६, १२
- १८ : १, २, ३, ६, ९, १८
सामाईक विभाजकांपैकी सर्वात मोठा **६** आहे. म्हणून मसावि = **६**.

## लसावि म्हणजे काय?
दोन किंवा अधिक संख्यांनी **पूर्ण भाग जाणारी सर्वात लहान संख्या** म्हणजे **लसावि** (लघुतम सामाईक विभाज्य).
१२ आणि १८ चा लसावि **३६** आहे, कारण ३६ ला १२ ने आणि १८ ने पूर्ण भाग जातो.

## लक्षात ठेवा
- मसावि हा दिलेल्या संख्यांपेक्षा लहान किंवा त्यांच्यातील लहान संख्येएवढा असतो.
- लसावि हा दिलेल्या संख्यांपेक्षा मोठा किंवा त्यांच्यातील मोठ्या संख्येएवढा असतो.
- दोन संख्यांचा गुणाकार = मसावि × लसावि (१२ × १८ = २१६ = ६ × ३६)`;

const MCQ_DATA = [
  {
    id: 'mcq1',
    question: 'दिलेल्या दोन संख्यांना पूर्ण भाग जाणारी सर्वात मोठी संख्या म्हणजे काय?',
    options: { A: 'लसावि', B: 'विभाज्य', C: 'मसावि', D: 'गुणाकार' },
    correctAnswer: 'C',
    explanation: "मसावि म्हणजे 'महत्तम सामाईक विभाजक' होय, जो दिलेल्या संख्यांना भाग देणारा सर्वात मोठा तुकडा असतो."
  },
  {
    id: 'mcq2',
    question: '४ आणि १० या संख्यांचा मसावि किती?',
    options: { A: '४', B: '२', C: '२०', D: '५' },
    correctAnswer: 'B',
    explanation: "४ चे अवयव २ × २ आणि १० चे अवयव २ × ५ आहेत, यात '२' हा एकमेव सामाईक विभाजक आहे."
  }
];

// Card 1 = Grammar (already done), card 2 = Maths (open)
const DEMO_TOPICS = [
  { subject: 'grammar', topic: 'jati', preDone: { correct: 8, total: 10 } },
  { subject: 'math', topic: 'lcm_hcf', preDone: null }
];

const DemoLekhiPage = ({ onBack }) => {
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const isMr = language === 'mr';

  useEffect(() => {
    preloadAudio.load();
  }, []);

  const [mathDone, setMathDone] = useState(null);       // { correct, total } once finished
  const [inTopic, setInTopic] = useState(false);
  const [topicDone, setTopicDone] = useState(new Set());
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [dotState, setDotState] = useState([]);
  const [result, setResult] = useState(null);
  const [justDone, setJustDone] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const scoreRef = useRef({ correct: 0, wrong: 0 });
  const snapshotRef = useRef(null);

  const t = isMr
    ? {
        day: 'दिवस', back: '← मागे', start: 'सुरू करा', done: '✓ पूर्ण',
        todayTopics: 'आजचे विषय', studyDay: 'अभ्यास दिवस', study: 'अभ्यास', minutes: 'मिनिटे',
        todayTopic: 'आजचा विषय', correct: 'बरोबर उत्तरे', dayDoneBanner: '✓ आजची लेखी पूर्ण!',
        shabash: 'शाबास!', shabashBtn: 'पुढे'
      }
    : {
        day: 'Day', back: '← Back', start: 'Start', done: '✓ Done',
        todayTopics: "Today's topics", studyDay: 'Study day', study: 'Study', minutes: 'min',
        todayTopic: "Today's topic", correct: 'correct answers', dayDoneBanner: "✓ Today's Lekhi is complete!",
        shabash: 'Shabash!', shabashBtn: 'Continue'
      };

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const blocks = [
    { id: 'audio1', icon: '/icon-audio.svg', type: 'audio', title: isMr ? 'शिकवणी' : 'Teaching', subtitle: isMr ? 'ऑडिओ' : 'Audio' },
    { id: 'studyText', icon: '/icon-study.svg', type: 'text', title: isMr ? 'पाठ्यपुस्तक' : 'Study text', subtitle: isMr ? 'वाचा' : 'Read' },
    { id: 'mcq', icon: '/icon-mcq.svg', type: 'mcq', title: 'MCQs', subtitle: `${MCQ_DATA.length} ${isMr ? 'प्रश्न' : 'questions'}` }
  ];

  const playClap = () => {
    try {
      const a = new Audio('/claps.mp3');
      a.play().catch(() => {});
    } catch (e) {}
  };

  // Score screen → 5 s → today screen (Maths card turns green)
  useEffect(() => {
    if (!result) return undefined;
    const id = setTimeout(() => {
      setMathDone(result);
      setResult(null);
      setInTopic(false);
      setJustDone('math');
    }, RESULT_SCREEN_MS);
    return () => clearTimeout(id);
  }, [result]);

  useEffect(() => {
    if (!justDone) return undefined;
    const id = setTimeout(() => setJustDone(null), 1500);
    return () => clearTimeout(id);
  }, [justDone]);

  // Both cards green → 3 s → celebration
  useEffect(() => {
    if (!mathDone || inTopic || result || showCelebration) return undefined;
    const id = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
      setShowCelebration(true);
      playClap();
    }, REWARD_DELAY_MS);
    return () => clearTimeout(id);
  }, [mathDone, inTopic, result, showCelebration]);

  const openMathTopic = () => {
    if (mathDone) return;
    scoreRef.current = { correct: 0, wrong: 0 };
    setDotState([]);
    setTopicDone(new Set());
    setActiveBlockId(null);
    setInTopic(true);
  };

  const recordItemResult = (itemId, correct) => {
    if (correct) scoreRef.current.correct++;
    else scoreRef.current.wrong++;
    setDotState(prev => [...prev, correct]);
  };

  const handleBlockTap = (block, index) => {
    if (topicDone.has(block.id)) return;
    const first = blocks.findIndex(b => !topicDone.has(b.id));
    if (index !== first) return;
    snapshotRef.current = { ...scoreRef.current, dots: dotState.length };
    setActiveBlockId(block.id);
  };

  const handleBlockBack = () => {
    const s = snapshotRef.current;
    if (s) {
      scoreRef.current = { correct: s.correct, wrong: s.wrong };
      setDotState(prev => prev.slice(0, s.dots));
    }
    setActiveBlockId(null);
  };

  const handleBlockComplete = () => {
    if (!activeBlockId) return;
    const next = new Set(topicDone);
    next.add(activeBlockId);
    setTopicDone(next);
    setActiveBlockId(null);
    snapshotRef.current = null;
    if (next.size >= blocks.length) {
      const { correct, wrong } = scoreRef.current;
      setResult({ correct, total: correct + wrong });
    }
  };

  const renderHeader = (completed, total, onBackTap) => (
    <div className="lk-header">
      <div className="lk-header-top">
        <button className="back-btn" onClick={onBackTap}>{t.back}</button>
        <button className="lang-toggle-lk" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>
      <div className="lk-header-center">
        <DonutProgress completed={completed} total={total} />
        <span className="lk-progress-text">{completed}/{total}</span>
      </div>
      <div className="lk-header-bottom">
        <div className="lk-left">
          <div className="lk-user-name">Virat Kohli</div>
          <div className="lk-date">{t.day} 15</div>
        </div>
        <div className="lk-day-badge">{t.day} 15 / 60</div>
      </div>
    </div>
  );

  const celebration = showCelebration && (
    <div className="demo-celebration-overlay">
      <div className="demo-confetti">
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className={`demo-confetti-piece piece-${i % 5}`} style={{ left: `${(i * 2.5) % 100}%`, animationDelay: `${(i % 10) * 0.15}s` }} />
        ))}
      </div>
      <div className="demo-celebration-box">
        <div className="demo-celebration-emoji">🎉</div>
        <div className="demo-celebration-text">{t.shabash}</div>
        <button className="demo-celebration-btn" onClick={onBack}>{t.shabashBtn}</button>
      </div>
    </div>
  );

  // ----- Score screen -----
  if (result) {
    return (
      <div {...CONTAINER_PROPS}>
        <LekhiWatermark />
        <div className="lkt-result lk-above-watermark">
          <div className="lkt-result-topic">
            {getSubjectName('math', language)} · {getTopicNameMr('lcm_hcf')}
          </div>
          <div className="lkt-result-circle">
            <span className="lkt-result-score">{result.correct}/{result.total}</span>
          </div>
          <div className="lkt-result-label">{t.correct}</div>
        </div>
      </div>
    );
  }

  // ----- Inside a block (demo: audio is free to skip) -----
  if (inTopic && activeBlockId) {
    const block = blocks.find(b => b.id === activeBlockId);
    const common = { language, onComplete: handleBlockComplete, onBack: handleBlockBack };
    if (block?.type === 'audio') {
      return <AudioView audio={AUDIO_DATA} blockLabel={block.title} topicName={getTopicNameMr('lcm_hcf')} {...common} />;
    }
    if (block?.type === 'text') {
      return <TextView textContent={{ text: STUDY_TEXT }} {...common} />;
    }
    if (block?.type === 'mcq') {
      return <MCQs mcqs={MCQ_DATA} recordItemResult={recordItemResult} dotState={dotState} totalDots={MCQ_DATA.length} {...common} />;
    }
  }

  // ----- Maths topic steps -----
  if (inTopic) {
    const firstOpen = blocks.findIndex(b => !topicDone.has(b.id));
    return (
      <div {...CONTAINER_PROPS}>
        <LekhiWatermark />
        {renderHeader(topicDone.size, blocks.length, () => setInTopic(false))}
        <div className="lk-topic-band lk-above-watermark">
          <div className="lk-topic-band-label">{t.todayTopic} · {t.study}</div>
          <div className="lk-topic-band-value">{getSubjectName('math', language)} - {getTopicNameMr('lcm_hcf')}</div>
        </div>
        <div className="lk-block-list lk-above-watermark">
          {blocks.map((block, i) => {
            const isCompleted = topicDone.has(block.id);
            const isActive = i === firstOpen;
            const isLocked = !isCompleted && !isActive;
            const iconSrc = isLocked ? '/icon-lock.svg' : block.icon;
            return (
              <div key={block.id} className={`lk-block-card ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}>
                <div className="lk-block-header">
                  <div className="lk-block-icon-wrap">
                    <img src={iconSrc} alt="" className="lk-block-icon-img" />
                  </div>
                  <div className="lk-block-info">
                    <div className="lk-block-name">{block.title}</div>
                    <div className="lk-block-subtitle">{block.subtitle}</div>
                  </div>
                </div>
                <div className="lk-block-controls">
                  {isCompleted ? (
                    <div className="lk-completed-indicator">{t.done}</div>
                  ) : isLocked ? null : (
                    <button className="lk-start-btn" onClick={() => handleBlockTap(block, i)}>{t.start}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ----- Today screen -----
  const doneCount = 1 + (mathDone ? 1 : 0);
  return (
    <div {...CONTAINER_PROPS}>
      <LekhiWatermark />
      {renderHeader(doneCount, 2, onBack)}

      <div className="lk-topic-band lk-above-watermark">
        <div className="lk-topic-band-label">{t.todayTopics}</div>
        <div className="lk-topic-band-value">{t.studyDay}</div>
      </div>

      <div className="lk-block-list lk-above-watermark">
        {mathDone && <div className="lkt-done-banner">{t.dayDoneBanner}</div>}

        {DEMO_TOPICS.map(tp => {
          const done = tp.subject === 'math' ? mathDone : tp.preDone;
          const tappable = !done;
          return (
            <div
              key={tp.subject}
              className={`lkt-card ${done ? 'done' : ''} ${justDone === tp.subject ? 'just-done' : ''} ${tappable ? 'tappable' : ''}`}
              onClick={() => { if (tappable) openMathTopic(); }}
            >
              <div className="lkt-card-subject">{getSubjectName(tp.subject, language)}</div>
              <div className="lkt-card-topic">{getTopicNameMr(tp.topic)}</div>
              <div className="lkt-card-meta">
                <span className="lkt-chip">{t.study}</span>
                {!done && <span className="lkt-chip">⏱ ~25 {t.minutes}</span>}
              </div>
              {done && <div className="lkt-card-status">✓ {done.correct}/{done.total}</div>}
              {tappable && <div className="lkt-card-arrow">→</div>}
            </div>
          );
        })}
      </div>

      {celebration}
    </div>
  );
};

export default DemoLekhiPage;